import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { X, Download, AlertTriangle, ExternalLink } from 'lucide-react';
import { documentApi } from '../lib/api';

// Preview an uploaded document inline. We fetch through the authenticated axios
// instance so cookie-auth is honored AND we bypass the download route's
// `Content-Disposition: attachment` header (which would otherwise force a
// download in a plain <img> / <iframe src=…> request).
export default function DocumentPreviewModal({ open, doc, fallbackName, onClose }) {
  const [state, setState] = useState('loading');   // loading | ready | error | unsupported
  const [url, setUrl] = useState('');
  const [mime, setMime] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    if (!open || !doc?.id) return undefined;
    let cancelled = false;
    setState('loading'); setErrMsg('');
    (async () => {
      try {
        const res = await documentApi.preview(doc.id);
        if (cancelled) return;
        // axios blob keeps the server's Content-Type on `blob.type`.
        const blob = res.data;
        const contentType = (blob && blob.type) || res.headers?.['content-type'] || '';
        const ct = String(contentType).split(';')[0].trim().toLowerCase();
        setMime(ct);
        const objUrl = URL.createObjectURL(blob);
        urlRef.current = objUrl;
        setUrl(objUrl);
        setState(ct.startsWith('image/') || ct === 'application/pdf' ? 'ready' : 'unsupported');
      } catch (e) {
        if (cancelled) return;
        const d = e?.response?.data;
        setErrMsg(typeof d === 'string' ? d : (d?.detail || 'Could not load document.'));
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
      // Free the object URL — the browser keeps blobs alive until we revoke.
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }
      setUrl('');
    };
  }, [open, doc?.id]);

  if (!open) return null;

  const filename = doc?.filename || fallbackName || 'document';

  return (
    <div data-testid="doc-view-modal"
         className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3">
      <div className="bg-white rounded-xl w-full max-w-[960px] max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E9F0] gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#11182B] truncate">{doc?.doc_type || 'Document preview'}</div>
            <div className="text-[11px] text-[#98A1B2] truncate">{filename}{mime ? ` · ${mime}` : ''}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {state === 'ready' && url && (
              // Anchor with download attribute — the object URL is served
              // in-tab, `download` forces the save dialog with the real name.
              <a data-testid="doc-view-download" href={url} download={filename}
                 className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E6E9F0] rounded-md text-[#2563EB] hover:bg-[#EFF1F6]">
                <Download className="w-4 h-4" /> Download
              </a>
            )}
            <button data-testid="doc-view-close" onClick={onClose}
                    className="text-[#5A6377] hover:text-[#11182B] p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-[#0B0F17] flex items-center justify-center">
          {state === 'loading' && (
            <div className="text-white text-sm py-16">Loading…</div>
          )}
          {state === 'error' && (
            <div className="flex items-start gap-2 max-w-md text-[13px] text-[#F6C453] bg-[#3A2A0A] border border-[#7A5B1A] rounded-md p-4">
              <AlertTriangle className="w-4 h-4 mt-[2px] flex-shrink-0" /><span>{errMsg}</span>
            </div>
          )}
          {state === 'ready' && mime.startsWith('image/') && (
            <img data-testid="doc-view-image"
                 src={url} alt={filename}
                 className="max-w-full h-auto bg-white" />
          )}
          {state === 'ready' && mime === 'application/pdf' && (
            <iframe data-testid="doc-view-iframe"
                    title={filename}
                    src={url}
                    className="w-full h-[80vh] bg-white" />
          )}
          {state === 'unsupported' && (
            <div className="max-w-md text-center text-sm text-white space-y-3 p-6">
              <div>Preview not available for this file type — use Download to open it locally.</div>
              {url && (
                <a data-testid="doc-view-open-external"
                   href={url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-white/40 rounded-md text-white hover:bg-white/10">
                  <ExternalLink className="w-4 h-4" /> Open in new tab
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
